import { FC, ReactNode } from "react";
import { Badge, Flex, Text } from "@chakra-ui/react";

import { CASE_FIELD_AUTHOR, MEDICAL_FIELD_OUTCOME, MedicalFieldResultModel } from "@shared/models";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";
import { MEDICAL_FIELD_LABELS } from "@containers/Cases/constants";
import { isMedicalListField, textToListItems } from "@containers/Cases/utils/caseDocuments";

import { CaseDocumentSavedLabel } from "../CaseDocumentModal";

const PENDING_NOTES: Record<CASE_FIELD_AUTHOR, string> = {
  [CASE_FIELD_AUTHOR.PSR]: "The PSR already has a value here. Nothing is overwritten until you choose.",
  [CASE_FIELD_AUTHOR.USER]:
    "This field already has a value entered by a user. Nothing is overwritten until you choose.",
};

const MutedLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <Text fontSize="13px" color="brand.emptyStateText">
    {children}
  </Text>
);

const WarningBadge: FC<{ children: ReactNode }> = ({ children }) => (
  <Badge bg="score.midBg" color="score.midText" fontWeight={FONT_WEIGHT.BOLD} letterSpacing="0.06em">
    {children}
  </Badge>
);

const OutcomeLabel: FC<Pick<MedicalFieldResultModel, "outcome" | "current_author" | "flag_reason">> = ({
  outcome,
  current_author,
  flag_reason,
}) => {
  if (flag_reason) return <WarningBadge>LOW CONFIDENCE</WarningBadge>;

  switch (outcome) {
    case MEDICAL_FIELD_OUTCOME.SAVED:
      return <CaseDocumentSavedLabel />;
    case MEDICAL_FIELD_OUTCOME.KEPT_CURRENT:
      return (
        <CaseDocumentSavedLabel label={current_author === CASE_FIELD_AUTHOR.PSR ? "Kept PSR" : "Kept current value"} />
      );
    case MEDICAL_FIELD_OUTCOME.PENDING:
      return <WarningBadge>NEEDS YOUR CHOICE</WarningBadge>;
    case MEDICAL_FIELD_OUTCOME.KEPT:
      return <MutedLabel>Kept your edit</MutedLabel>;
    default:
      return <MutedLabel>Nothing found</MutedLabel>;
  }
};

interface MedicalRecordsFieldResultProps {
  result: MedicalFieldResultModel;
}

const MedicalRecordsFieldResult: FC<MedicalRecordsFieldResultProps> = ({ result }) => {
  const { field, outcome, text, current_author, flag_reason } = result;

  return (
    <Flex
      direction="column"
      gap="6px"
      px="18px"
      py={4}
      borderBottom="1px solid"
      borderColor="gray.200"
      _last={{ borderBottom: "none" }}
    >
      <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
        <Text fontSize="15px" fontWeight={FONT_WEIGHT.HEAVY} color="brand.modalHeaderText">
          {MEDICAL_FIELD_LABELS[field]}
        </Text>
        <OutcomeLabel outcome={outcome} current_author={current_author} flag_reason={flag_reason} />
      </Flex>
      {text && (
        <Text fontSize="13px" lineHeight={1.5} color="brand.textSecondary" lineClamp={2}>
          {isMedicalListField(field) ? textToListItems(text).join("; ") : text}
        </Text>
      )}
      {flag_reason && (
        <Text fontSize="12px" lineHeight={1.5} color="score.midText">
          {flag_reason}
        </Text>
      )}
      {outcome === MEDICAL_FIELD_OUTCOME.PENDING && (
        <Text fontSize="12px" color="brand.emptyStateText">
          {PENDING_NOTES[current_author ?? CASE_FIELD_AUTHOR.USER]}
        </Text>
      )}
    </Flex>
  );
};

export default MedicalRecordsFieldResult;
